import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AlertTriangle } from "lucide-react";
import { useSelector } from "react-redux";

import messages from "../../../../../assets/locale/messages";
import { UIActionModal, UICallout, UIRadioGroup } from "../../../../ui";
import { getTagEligibility } from "../tagEligibility";
import "./TagModals.scss";

const fill = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, value),
    template ?? "",
  );

/** Fill a template then render `<b>…</b>` segments as highlighted spans. */
const renderHighlighted = (template, values) => {
  const filled = fill(template, values);
  const nodes = [];
  let lastIndex = 0;
  let key = 0;
  for (const match of filled.matchAll(/<b>(.*?)<\/b>/g)) {
    if (match.index > lastIndex) {
      nodes.push(filled.slice(lastIndex, match.index));
    }
    nodes.push(
      <span key={`h${key}`} className="event-applicants-tag-callout__highlight">
        {match[1]}
      </span>,
    );
    key += 1;
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < filled.length) nodes.push(filled.slice(lastIndex));
  return nodes;
};

const ApplicantTagModal = ({
  open,
  userTag,
  isBulk,
  selectedApplicants,
  tagOptions,
  portalVariant,
  myCompanyId,
  onClose,
  onSave,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const t =
    messages[lang].events.viewEvents.applicantsTab.joiningRequests.tagModal;

  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (isBulk || !userTag?.tag) {
      setSelectedTag(null);
      return;
    }
    const existing = tagOptions.find(
      (option) => String(option.id) === String(userTag.tag.id),
    );
    setSelectedTag(existing || null);
  }, [open, isBulk, userTag, tagOptions]);

  const hasExistingTag = isBulk
    ? selectedApplicants.some((applicant) => applicant.userTag)
    : Boolean(userTag);
  const title = hasExistingTag ? t.titleEdit : t.titleAdd;

  const bulkInfo = useMemo(() => {
    if (!isBulk) return null;
    return getTagEligibility(selectedApplicants, {
      portalVariant,
      myCompanyId,
      mode: "assign",
    });
  }, [isBulk, selectedApplicants, portalVariant, myCompanyId]);

  // Only tags owned by another company are skipped on a bulk assign.
  const skippedCount = bulkInfo ? bulkInfo.otherOwnerCount : 0;
  const someTagged =
    isBulk && bulkInfo && skippedCount > 0 && bulkInfo.eligibleUserIds.length > 0;
  const allAlreadyTagged =
    isBulk && bulkInfo && bulkInfo.eligibleUserIds.length === 0 && skippedCount > 0;

  const radioOptions = useMemo(
    () => tagOptions.map((option) => ({ value: option.id, label: option.name })),
    [tagOptions],
  );

  return (
    <UIActionModal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="sm"
      actions={
        <UIActionModal.Actions
          cancelLabel={t.cancel}
          confirmLabel={t.save}
          confirmDisabled={!selectedTag?.id || allAlreadyTagged}
          onCancel={onClose}
          onConfirm={() => onSave(selectedTag?.id)}
        />
      }
      PaperProps={{ className: "event-applicants-tag-modal" }}
    >
      <UIActionModal.Divider />
      {allAlreadyTagged && (
        <UICallout
          variant="warning"
          className="event-applicants-tag-callout event-applicants-tag-callout--warning"
          icon={
            <AlertTriangle
              size={16}
              strokeWidth={1.5}
              color="var(--color-warning-default, #F59E0B)"
            />
          }
          description={fill(t.allAlreadyTagged, { count: skippedCount })}
        />
      )}
      {someTagged && (
        <UICallout
          variant="warning"
          className="event-applicants-tag-callout event-applicants-tag-callout--warning"
          icon={
            <AlertTriangle
              size={16}
              strokeWidth={1.5}
              color="var(--color-warning-default, #F59E0B)"
            />
          }
          description={renderHighlighted(t.assignSkipInfo, {
            count: skippedCount,
            total: bulkInfo.total,
            remaining: bulkInfo.eligibleUserIds.length,
          })}
        />
      )}
      <div className="event-applicants-tag-modal__field">
        <UIRadioGroup
          label={t.hint}
          value={selectedTag?.id ?? ""}
          options={radioOptions}
          onChange={(nextValue) =>
            setSelectedTag(
              tagOptions.find(
                (option) => String(option.id) === String(nextValue),
              ) || null,
            )
          }
        />
      </div>
    </UIActionModal>
  );
};

ApplicantTagModal.propTypes = {
  open: PropTypes.bool.isRequired,
  userTag: PropTypes.object,
  isBulk: PropTypes.bool,
  selectedApplicants: PropTypes.arrayOf(PropTypes.object),
  tagOptions: PropTypes.arrayOf(PropTypes.object),
  portalVariant: PropTypes.string,
  myCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

ApplicantTagModal.defaultProps = {
  userTag: null,
  isBulk: false,
  selectedApplicants: [],
  tagOptions: [],
  portalVariant: "company-portal",
  myCompanyId: undefined,
};

export default ApplicantTagModal;
